<script setup lang="ts">
	import { computed, ref, toRef, watch } from 'vue'

	import useDestinationPhoto from '../composables/useDestinationPhoto'
	import type { HotelSearchCoverSource } from '../interfaces/recentHotelSearch'

	/**
	 * Portada de una tarjeta de «Últimas búsquedas»: la foto que BookingV2 guarda una vez
	 * (fachada del hotel o foto del destino); si no hay, o no carga, el diseño azul con
	 * el ícono. También la usan las tarjetas de «Estadísticas de hoteles» en Mi empresa.
	 * Lo que va encima (el ícono de reservada) entra por el slot.
	 */
	const props = defineProps<{ search: HotelSearchCoverSource }>()

	const { photoUrl } = useDestinationPhoto(toRef(props, 'search'))

	// Una foto que no cargó cae al diseño sin imagen en vez de enseñar el ícono de
	// imagen rota. Se olvida si la URL cambia.
	const broken = ref(false)
	watch(photoUrl, () => (broken.value = false))

	const hasImage = computed(() => !!photoUrl.value && !broken.value)

	const placeIcon = computed(() =>
		props.search.destinationType === 'hotel' ? 'apartment' : 'location_on'
	)

	// Pie de la portada: la ciudad y el país, que el título largo no deja ver.
	const caption = computed(() =>
		[props.search.city, props.search.countryCode].filter(Boolean).join(' · ')
	)
</script>

<template>
	<span
		class="relative block w-24 shrink-0 self-stretch overflow-hidden bg-gradient-to-br from-[#253C70] to-[#3d5a99] sm:h-28 sm:w-full"
	>
		<template v-if="hasImage">
			<img
				:src="photoUrl ?? ''"
				alt=""
				loading="lazy"
				class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
				@error="broken = true"
			/>
			<!-- Sobre la foto el pie necesita fondo para leerse. -->
			<span
				v-if="caption"
				class="pointer-events-none absolute inset-x-0 bottom-0 hidden bg-gradient-to-t from-black/60 to-transparent px-3 pb-2 pt-6 sm:block"
			>
				<span class="block max-w-[75%] truncate text-xs font-medium text-white">
					{{ caption }}
				</span>
			</span>
		</template>
		<template v-else>
			<span
				class="material-symbols-outlined pointer-events-none absolute -bottom-3 -right-2 text-[72px] leading-none text-white/20 sm:text-[96px]"
				aria-hidden="true"
				>{{ placeIcon }}</span
			>
			<span
				class="material-symbols-outlined absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[28px] text-white/90 sm:hidden"
				aria-hidden="true"
				>{{ placeIcon }}</span
			>
			<span
				v-if="caption"
				class="absolute bottom-2 left-3 hidden max-w-[75%] truncate text-xs font-medium text-white/90 sm:block"
				>{{ caption }}</span
			>
		</template>

		<slot />
	</span>
</template>
